/**
 * Utilities for finding and modifying `.claude/settings.local.json`
 * to grant screenshot read permissions.
 *
 * @module
 */

import fs from 'node:fs';
import path from 'node:path';
import os from 'node:os';

/** Walk up from `startDir` looking for a `.claude/` directory. */
export function findClaudeSettingsDir(startDir: string = process.cwd()): string | null {
  let dir = path.resolve(startDir);
  const root = path.parse(dir).root;

  while (dir !== root) {
    const candidate = path.join(dir, '.claude');
    if (fs.existsSync(candidate) && fs.statSync(candidate).isDirectory()) {
      return candidate;
    }
    dir = path.dirname(dir);
  }
  return null;
}

/** The predictable screenshot directory path. */
export function getScreenshotDirPath(): string {
  return path.join(os.homedir(), '.canary', 'screenshots');
}

/** The Read permission pattern for the screenshot directory. */
export function getScreenshotReadPattern(): string {
  return `Read(${getScreenshotDirPath()}/**)`;
}

/** Check if the screenshot Read permission is already configured. */
export function hasScreenshotPermission(settingsDir: string): boolean {
  const filePath = path.join(settingsDir, 'settings.local.json');
  if (!fs.existsSync(filePath)) return false;

  try {
    const content = JSON.parse(fs.readFileSync(filePath, 'utf-8'));
    const allow: unknown[] = content?.permissions?.allow ?? [];
    const pattern = getScreenshotReadPattern();
    return allow.includes(pattern);
  } catch {
    return false;
  }
}

/** Add the screenshot Read permission to settings.local.json. Idempotent. */
export function addScreenshotPermission(settingsDir: string): {
  added: string | null;
  alreadyPresent: boolean;
} {
  const filePath = path.join(settingsDir, 'settings.local.json');
  const pattern = getScreenshotReadPattern();

  let content: Record<string, unknown> = {};
  if (fs.existsSync(filePath)) {
    try {
      content = JSON.parse(fs.readFileSync(filePath, 'utf-8'));
    } catch (err) {
      throw new Error(
        `Failed to parse ${filePath}: ${err instanceof Error ? err.message : String(err)}`
      );
    }
  }

  // Ensure permissions.allow exists
  if (!content.permissions || typeof content.permissions !== 'object') {
    content.permissions = { allow: [], deny: [], ask: [] };
  }
  const permissions = content.permissions as Record<string, unknown>;
  if (!Array.isArray(permissions.allow)) {
    permissions.allow = [];
  }
  const allow = permissions.allow as string[];

  if (allow.includes(pattern)) {
    return { added: null, alreadyPresent: true };
  }

  allow.push(pattern);
  fs.writeFileSync(filePath, JSON.stringify(content, null, 2) + '\n', 'utf-8');
  return { added: pattern, alreadyPresent: false };
}
